// Shared equipment PAPERDOLL — the WoW/Rust-style figure card: the character centred (live idle
// preview), worn GARMENTS down the left (Head/Chest/Legs/Feet), ACCESSORY slots down the right
// (Neck/Wrist/Wrist, reserved), and weapon HAND slots under the figure. Hovering an equipped item
// shows its detail tooltip. Used by the inventory (interactive — click to unequip) AND the player
// inspect window (read-only), so the two look identical.

import {
  C3D_IDLE_FRAMES, C3D_IDLE_MS, C3D_CELL, C3D_COLS, Direction, c3dIdleFrame,
  equipToItem, gearStat, itemEquip, applyEquip, isHandItem, isPetItem, isStatGear, cosmeticSlotToEquip,
  ITEM_NAMES, ITEM_DESCRIPTIONS, ITEM_WEIGHT_KG,
  type CharacterAppearance, type EquipSlot, type HandSlot, type CosmeticSlot, type ItemMods,
  FISHING_ROD_ID,
  ROD_BAIT_MAX,
  FISHING_HAT_ID,
  FISH_HAT_ZONE_BONUS,
  FISH_HAT_SPEED_MULT,
} from "@ghostland/shared";

/** A slot a paperdoll cell can unequip into / be a drag target for. */
type DollSlot = EquipSlot | HandSlot | "neck" | CosmeticSlot;
function isCosmeticSlot(s: DollSlot): s is CosmeticSlot {
  return s === "cosHat" || s === "cosTop" || s === "cosBottom" || s === "cosShoes";
}
function isGearGarment(s: DollSlot): s is EquipSlot {
  return s === "hat" || s === "top" || s === "bottom" || s === "shoes" || s === "mask" || s === "back";
}

/** May this bag item be equipped into the given slot? Drives the drag-to-equip drop-hint + accept.
 *  GEAR slots take only stat gear (leather/armored); COSMETIC slots take only non-stat garments.
 *  MASK/BACK have no cosmetic counterpart — any matching garment goes straight to the slot. */
function canEquipTo(itemId: string, slot: DollSlot): boolean {
  if (slot === "neck") return isPetItem(itemId);          // the Hog Charm
  if (slot === "hand1") return isHandItem(itemId) || itemId === FISHING_ROD_ID; // weapon/shield — or the rod (primary only)
  if (slot === "hand2") return isHandItem(itemId);
  if (slot === "mask" || slot === "back") return itemEquip(itemId)?.slot === slot;
  if (isCosmeticSlot(slot)) return !isStatGear(itemId) && itemEquip(itemId)?.slot === cosmeticSlotToEquip(slot);
  return isStatGear(itemId) && itemEquip(itemId)?.slot === slot; // gear garment: stat gear only
}
import { itemThumb } from "../items/itemThumb.js";
import { compositeAtlas } from "../sprites/c3dPipeline.js";

const FIG = { sx: 44, sy: 2, sw: 56, sh: 98 }; // head→feet crop within a 144px idle cell
const GARMENTS: { slot: EquipSlot; label: string }[] = [
  { slot: "hat", label: "Head" }, { slot: "top", label: "Chest" },
  { slot: "bottom", label: "Legs" }, { slot: "shoes", label: "Feet" },
];
const COSMETICS: { slot: CosmeticSlot; label: string }[] = [
  { slot: "cosHat", label: "Head" }, { slot: "cosTop", label: "Chest" },
  { slot: "cosBottom", label: "Legs" }, { slot: "cosShoes", label: "Feet" },
];
type CosmeticData = { hat: string; top: string; bottom: string; shoes: string };
const NO_COSMETICS: CosmeticData = { hat: "", top: "", bottom: "", shoes: "" };
function cosItem(cos: CosmeticData, slot: CosmeticSlot): string {
  return slot === "cosHat" ? cos.hat : slot === "cosTop" ? cos.top : slot === "cosBottom" ? cos.bottom : cos.shoes;
}
const HANDS: { slot: HandSlot; label: string }[] = [
  { slot: "hand1", label: "First Hand" }, { slot: "hand2", label: "Second Hand" },
];

const STYLE_ID = "ghostland-paperdoll-style";
const CSS = `
.gl-pd {
  display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-radius: 12px;
  background: radial-gradient(ellipse 64% 56% at 50% 40%, rgba(124,58,237,.12), transparent 72%), linear-gradient(#0c0b12, #07070b);
  border: 1px solid var(--gl-border); box-shadow: inset 0 0 0 1px #000;
}
.gl-pd-side { display: flex; flex-direction: column; gap: 16px; }
.gl-pd-coltitle { font-family: 'Silkscreen', ui-monospace, monospace; font-size: 7px; letter-spacing: .14em; text-transform: uppercase; color: var(--gl-muted); text-align: center; margin-bottom: -6px; }
.gl-pd-side.dim { opacity: .42; }
.gl-pd-fig {
  position: relative; width: 134px; height: 232px; flex: none;
  display: flex; align-items: flex-end; justify-content: center; padding-bottom: 8px;
  background: radial-gradient(ellipse 70% 60% at 50% 56%, rgba(124,58,237,.10), transparent 70%), #05050a;
  border: 2px solid #2a2433; border-radius: 10px;
  box-shadow: inset 0 0 28px rgba(0,0,0,.7), inset 0 0 0 1px rgba(201,169,106,.14);
}
.gl-pd-fig::before, .gl-pd-fig::after { content: ""; position: absolute; width: 10px; height: 10px; border: 2px solid #c9a96a; opacity: .55; }
.gl-pd-fig::before { top: 5px; left: 5px; border-right: none; border-bottom: none; }
.gl-pd-fig::after  { bottom: 5px; right: 5px; border-left: none; border-top: none; }
.gl-pd-preview { image-rendering: pixelated; width: 114px; height: 200px; }
.gl-pd-mid { display: flex; flex-direction: column; align-items: center; gap: 18px; }
.gl-pd-hands { display: flex; gap: 18px; }
.gl-eq {
  position: relative; width: 52px; height: 52px;
  display: flex; align-items: center; justify-content: center;
  background: var(--gl-panel-2); border: 2px solid #322b3a; border-radius: 8px; box-shadow: inset 0 0 0 2px #000;
}
.gl-eq canvas { image-rendering: pixelated; width: 42px; height: 42px; }
.gl-eq.filled { border-color: #c9a96a; box-shadow: inset 0 0 0 2px #000, 0 0 10px rgba(201,169,106,.28); }
.gl-eq.clickable.filled { cursor: pointer; }
.gl-eq.clickable.filled:hover { border-color: #e7ce92; }
.gl-eq.drop-hint { border-color: var(--gl-accent, #b48cff); box-shadow: inset 0 0 0 2px #000, 0 0 12px rgba(180,140,255,.55); }
.gl-eq.acc { border-style: dashed; border-color: #2c2738; }
.gl-eq .gl-eq-ph { font-size: 19px; color: rgba(255,255,255,.13); line-height: 1; }
.gl-eq .gl-eq-label {
  position: absolute; left: -2px; right: -2px; bottom: -13px; text-align: center;
  font-size: 7px; letter-spacing: .12em; text-transform: uppercase; color: var(--gl-muted);
  font-family: 'Silkscreen', ui-monospace, monospace;
}
#gl-pd-tip {
  position: fixed; z-index: 36; display: none; max-width: 220px; pointer-events: none;
  padding: 8px 10px; background: var(--gl-panel); border: 1px solid var(--gl-border-focus);
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.6); font-family: ui-monospace, monospace;
}
#gl-pd-tip.open { display: block; }
#gl-pd-tip .nm { font-size: 12px; font-weight: 700; color: var(--gl-text); }
#gl-pd-tip .mt { font-size: 10px; color: var(--gl-accent, #b48cff); margin-top: 2px; letter-spacing: .5px; }
#gl-pd-tip .st { font-size: 11px; margin-top: 5px; }
#gl-pd-tip .st .base { color: var(--gl-text); }
#gl-pd-tip .st .roll { color: #7ee08a; }
#gl-pd-tip .ds { font-size: 11px; color: var(--gl-muted); margin-top: 5px; line-height: 1.35; }
`;

const STAT_NAMES: Record<string, string> = { strength: "Strength", dex: "Dexterity", agility: "Agility" };
/** Gear stat block for the hover tooltip: base "+N Armor" + "+1 <Stat>", plus any rolled armored mods. */
function fishHatHtml(itemId: string): string {
  if (itemId !== FISHING_HAT_ID) return "";
  return `<div class="st"><span class="roll">+${(FISH_HAT_ZONE_BONUS * 10).toFixed(1)} Fishing power</span></div>` +
    `<div class="st"><span class="roll">\u2212${Math.round((1 - FISH_HAT_SPEED_MULT) * 100)}% Fish movement speed</span></div>`;
}

function gearStatHtml(itemId: string, mods?: ItemMods): string {
  const g = gearStat(itemId);
  if (!g) return "";
  const rolled = mods
    ? (["strength", "dex", "agility"] as const).filter((k) => (mods[k] ?? 0) > 0).map((k) => `+${mods[k]} ${STAT_NAMES[k]}`).join(" ")
    : "";
  return `<div class="st"><span class="base">+${g.armor} Armor</span> <span class="roll">+${g.amount} ${STAT_NAMES[g.stat] ?? g.stat}${rolled ? ` ${rolled}` : ""}</span></div>`;
}

export function injectPaperdollStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
}

// --- shared hover tooltip (item detail) ---
let tipEl: HTMLDivElement | null = null;
function ensureTip(): HTMLDivElement {
  if (!tipEl) { tipEl = document.createElement("div"); tipEl.id = "gl-pd-tip"; document.body.appendChild(tipEl); }
  return tipEl;
}
function showTip(itemId: string, x: number, y: number, mods?: ItemMods, bait?: number): void {
  const t = ensureTip();
  const name = ITEM_NAMES[itemId as keyof typeof ITEM_NAMES] ?? itemId;
  const desc = ITEM_DESCRIPTIONS[itemId as keyof typeof ITEM_DESCRIPTIONS] ?? "";
  const baits = bait !== undefined ? `<div class="st"><span class="roll">Baits ${bait}/${ROD_BAIT_MAX}</span></div>` : "";
  t.innerHTML = `<div class="nm">${name}</div><div class="mt">Weight ${ITEM_WEIGHT_KG}kg</div>${gearStatHtml(itemId, mods)}${fishHatHtml(itemId)}${baits}${desc ? `<div class="ds">${desc}</div>` : ""}`;
  t.classList.add("open"); moveTip(x, y);
}
function moveTip(x: number, y: number): void {
  if (!tipEl || !tipEl.classList.contains("open")) return;
  const pad = 14, w = tipEl.offsetWidth, h = tipEl.offsetHeight;
  let left = x + pad, top = y + pad;
  if (left + w > window.innerWidth - 8) left = x - pad - w;
  if (top + h > window.innerHeight - 8) top = y - pad - h;
  tipEl.style.left = `${Math.max(8, left)}px`; tipEl.style.top = `${Math.max(8, top)}px`;
}
function hideTip(): void { tipEl?.classList.remove("open"); }

export interface PaperdollOpts {
  /** Inventory mode: clicking a filled garment/hand/neck/cosmetic slot unequips it. Omit for read-only. */
  onUnequip?: (slot: DollSlot) => void;
  /** Inventory mode: right-clicking the worn NECK charm uses it (summon/dismiss the pet). */
  onUseNeck?: () => void;
  /** Inventory mode: dropping a dragged BAG item onto a matching gear slot equips it (by bag slot index). */
  onEquip?: (fromSlotIndex: number) => void;
  /** Returns the itemId currently being dragged from the bag (or null) — used to validate drop targets,
   *  since `dragover` can't read the drag payload. */
  draggingItemId?: () => string | null;
}

function el(tag: string, cls: string): HTMLDivElement {
  const e = document.createElement(tag) as HTMLDivElement; e.className = cls; return e;
}

export class Paperdoll {
  readonly root: HTMLDivElement;
  private readonly left: HTMLDivElement;
  private readonly cosmeticCol: HTMLDivElement;
  private readonly right: HTMLDivElement;
  private readonly handsEl: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private appearance: CharacterAppearance | null = null;
  private hands = { hand1: "", hand2: "" };
  private rodBait = 0; // bait in the equipped rod (hand1) — shown in its hover tooltip
  private neck = ""; // itemId worn in the neck accessory slot ("" = empty)
  private cos: CosmeticData = { ...NO_COSMETICS }; // cosmetic garment overrides per slot ("" = none)
  private showCos = true; // whether cosmetics are shown (figure preview + cell dimming)
  private mods: { top?: ItemMods; bottom?: ItemMods } = {}; // rolled armored bonus on the worn top/bottom
  private atlas: HTMLCanvasElement | null = null;
  private atlasGen = 0; private frame = 0; private clock = 0; private lastTs = 0; private raf = 0;

  constructor(private readonly opts: PaperdollOpts = {}) {
    injectPaperdollStyle();
    this.root = el("div", "gl-pd");
    this.left = el("div", "gl-pd-side");
    this.cosmeticCol = el("div", "gl-pd-side");
    const mid = el("div", "gl-pd-mid");
    const fig = el("div", "gl-pd-fig");
    this.canvas = document.createElement("canvas");
    this.canvas.width = FIG.sw; this.canvas.height = FIG.sh; this.canvas.className = "gl-pd-preview";
    fig.appendChild(this.canvas);
    this.handsEl = el("div", "gl-pd-hands");
    mid.append(fig, this.handsEl);
    this.right = el("div", "gl-pd-side");
    // Left-to-right: cosmetics, then gear, then the figure, then accessories (neck/wrist).
    this.root.append(this.cosmeticCol, this.left, mid, this.right);
  }

  private locked: ReadonlySet<EquipSlot> = new Set();

  /** Feed the doll a character: its worn garments + held weapons. `locked` slots show a 🔒 and can't be
   *  unequipped (used to pin the work uniform while clocked in). */
  setData(a: CharacterAppearance, hands: { hand1: string; hand2: string; rodBait?: number }, neck = "", locked?: ReadonlySet<EquipSlot>, mods?: { top?: ItemMods; bottom?: ItemMods }, cos?: CosmeticData, showCos = true): void {
    this.appearance = a; this.hands = hands; this.rodBait = hands.rodBait ?? 0; this.neck = neck; this.locked = locked ?? new Set(); this.mods = mods ?? {};
    this.cos = cos ?? { ...NO_COSMETICS }; this.showCos = showCos;
    this.render();
    // The figure shows the DISPLAYED look: gear with cosmetics overlaid (when shown).
    let display = a;
    if (showCos) for (const id of [this.cos.hat, this.cos.top, this.cos.bottom, this.cos.shoes]) {
      const eq = id ? itemEquip(id) : undefined;
      if (eq) display = applyEquip(display, eq);
    }
    void this.rebuildAtlas(display);
  }

  private label(text: string): HTMLSpanElement {
    const l = document.createElement("span"); l.className = "gl-eq-label"; l.textContent = text; return l;
  }

  private cell(itemId: string | null, label: string, unequip: DollSlot | null, acc = false, mods?: ItemMods): HTMLDivElement {
    const isLocked = !!unequip && isGearGarment(unequip) && this.locked.has(unequip);
    const clickable = unequip && this.opts.onUnequip && !isLocked;
    const c = el("div", "gl-eq" + (acc ? " acc" : "") + (clickable ? " clickable" : ""));
    if (isLocked) { c.style.opacity = "0.6"; c.title = "Locked while on shift"; }
    if (itemId) {
      c.classList.add("filled");
      c.appendChild(itemThumb(itemId));
      const bait = unequip === "hand1" && itemId === FISHING_ROD_ID ? this.rodBait : undefined;
      c.addEventListener("mouseenter", (e) => showTip(itemId, e.clientX, e.clientY, mods, bait));
      c.addEventListener("mousemove", (e) => moveTip(e.clientX, e.clientY));
      c.addEventListener("mouseleave", hideTip);
      if (clickable) c.addEventListener("click", () => { hideTip(); this.opts.onUnequip!(unequip!); });
      if (isLocked) { const lk = document.createElement("span"); lk.textContent = "🔒"; lk.style.cssText = "position:absolute;top:2px;right:3px;font-size:11px"; c.appendChild(lk); }
    } else {
      const ph = document.createElement("span"); ph.className = "gl-eq-ph"; ph.textContent = "+"; c.appendChild(ph);
    }
    // Drag-to-equip: a matching bag item dropped here equips it (into an empty slot, or swapping a filled
    // one). Skipped for reserved/locked slots and in read-only mode. `dragover` can't read the payload,
    // so we validate against the live dragged itemId; only a valid target previewable + accepts the drop.
    if (unequip && !isLocked && this.opts.onEquip) {
      const slotKey = unequip;
      c.addEventListener("dragover", (e) => {
        const dragId = this.opts.draggingItemId?.();
        if (!dragId || !canEquipTo(dragId, slotKey)) return; // invalid → no preventDefault ⇒ "no-drop" cursor
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
        c.classList.add("drop-hint");
      });
      c.addEventListener("dragleave", () => c.classList.remove("drop-hint"));
      c.addEventListener("drop", (e) => {
        e.preventDefault();
        c.classList.remove("drop-hint");
        const from = Number(e.dataTransfer?.getData("text/plain"));
        const dragId = this.opts.draggingItemId?.();
        if (Number.isInteger(from) && dragId && canEquipTo(dragId, slotKey)) this.opts.onEquip!(from);
      });
    }
    c.appendChild(this.label(label));
    return c;
  }

  private render(): void {
    const a = this.appearance;
    if (!a) return;
    hideTip(); // cells are rebuilt; a hovered slot vanishes without a mouseleave (stuck-tooltip fix)
    const worn: Record<EquipSlot, [number, number]> = {
      hat: [a.hat, a.hatColor], top: [a.top, a.topColor], bottom: [a.bottom, a.bottomColor], shoes: [a.shoes, a.shoesColor],
      mask: [a.mask, a.maskColor], back: [a.backAcc, a.backAccColor],
    };
    const title = (text: string): HTMLDivElement => { const t = el("div", "gl-pd-coltitle"); t.textContent = text; return t; };
    // GEAR column (left): stat gear only — leather/armored top & bottom (Head/Feet reserved for future gear).
    this.left.replaceChildren(title("Gear"), ...GARMENTS.map(({ slot, label }) =>
      this.cell(equipToItem(slot, ...worn[slot]) ?? null, label, slot, false, slot === "top" ? this.mods.top : slot === "bottom" ? this.mods.bottom : undefined)));
    // COSMETIC column: visual overrides per slot. Dimmed when cosmetics are toggled off (still equipped).
    this.cosmeticCol.classList.toggle("dim", !this.showCos);
    this.cosmeticCol.replaceChildren(title("Cosmetic"), ...COSMETICS.map(({ slot, label }) =>
      this.cell(cosItem(this.cos, slot) || null, label, slot, true)));
    // Right column: a real NECK accessory slot (worn charm — click to unequip, right-click to
    // summon/dismiss), then the MASK (face — balaclava etc.) + BACK (cape/backpack) slots.
    const neckCell = this.cell(this.neck || null, "Neck", "neck", true);
    if (this.neck && this.opts.onUseNeck) {
      neckCell.title = "Right-click to summon / dismiss";
      neckCell.addEventListener("contextmenu", (e) => { e.preventDefault(); this.opts.onUseNeck!(); });
    }
    this.right.replaceChildren(
      neckCell,
      this.cell(equipToItem("mask", ...worn.mask) ?? null, "Mask", "mask", true),
      this.cell(equipToItem("back", ...worn.back) ?? null, "Back", "back", true),
    );
    this.handsEl.replaceChildren(...HANDS.map(({ slot, label }) => this.cell((slot === "hand1" ? this.hands.hand1 : this.hands.hand2) || null, label, slot)));
  }

  private async rebuildAtlas(a: CharacterAppearance): Promise<void> {
    const gen = ++this.atlasGen;
    const canvas = await compositeAtlas(a);
    if (gen !== this.atlasGen) return;
    this.atlas = canvas;
    this.draw();
  }

  private draw(): void {
    if (!this.atlas) return;
    const ctx = this.canvas.getContext("2d")!; ctx.imageSmoothingEnabled = false;
    const idx = c3dIdleFrame(Direction.Down, this.frame), col = idx % C3D_COLS, row = Math.floor(idx / C3D_COLS);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.atlas, col * C3D_CELL + FIG.sx, row * C3D_CELL + FIG.sy, FIG.sw, FIG.sh, 0, 0, this.canvas.width, this.canvas.height);
  }

  /** Begin the idle preview animation. */
  start(): void {
    if (this.raf) return;
    this.lastTs = 0;
    const tick = (ts: number): void => {
      if (!this.raf) return;
      if (this.lastTs) { this.clock += ts - this.lastTs; while (this.clock >= C3D_IDLE_MS) { this.clock -= C3D_IDLE_MS; this.frame = (this.frame + 1) % C3D_IDLE_FRAMES; } }
      this.lastTs = ts; this.draw(); this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = 0; }
    hideTip();
  }

  dispose(): void { this.stop(); this.root.remove(); }
}
